import React, { useState, useMemo, useCallback } from 'react';
import './App.css';
import arorAppsLogo from './assets/aror_apps logo.png';
import { apps, CATEGORIES } from './data/apps';

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const ExternalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M7 7h10v10" />
    <path d="M7 17 17 7" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const ShareIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <path d="M8.59 13.51 15.42 17.49" />
    <path d="M15.41 6.51 8.59 10.49" />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const AndroidIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85a.637.637 0 00-.83.22l-1.88 3.24a11.463 11.463 0 00-8.94 0L5.65 5.67a.643.643 0 00-.87-.2c-.28.18-.37.54-.22.83L6.4 9.48A10.78 10.78 0 001 18h22a10.78 10.78 0 00-5.4-8.52zM7 15.25a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5zm10 0a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5z" />
  </svg>
);

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  }
}

function LinkActions({ url, title, variant = 'card' }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const success = await copyToClipboard(url);
    if (success) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  }, [url]);

  const handleShare = useCallback(async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }

    const success = await copyToClipboard(url);
    if (success) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  }, [title, url]);

  return (
    <div
      className={`link-actions link-actions--${variant}`}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <button type="button" className="link-actions__btn" onClick={handleShare} aria-label={`Share ${title}`}>
        <ShareIcon />
        <span>Share</span>
      </button>
      <button type="button" className="link-actions__btn" onClick={handleCopy} aria-label={`Copy link for ${title}`}>
        <CopyIcon />
        <span>{copied ? 'Copied' : 'Copy link'}</span>
      </button>
    </div>
  );
}

function Header() {
  const portalUrl = window.location.href;

  return (
    <header className="portal-header">
      <div className="portal-header__accent" aria-hidden="true" />
      <div className="portal-header__inner">
        <div className="portal-header__brand">
          <div className="portal-header__logo-wrap">
            <img src={arorAppsLogo} alt="AROR University Apps" className="portal-header__logo" />
          </div>
          <div className="portal-header__text">
            <h1 className="portal-header__title">
              Aror University <span className="portal-header__title-accent">Apps</span>
            </h1>
            <p className="portal-header__tagline">
              Your gateway to all university applications
            </p>
            <LinkActions
              url={portalUrl}
              title="Aror University Apps"
              variant="header"
            />
          </div>
        </div>
        <div className="portal-header__divider" aria-hidden="true">
          <span className="portal-header__divider-line" />
          <span className="portal-header__divider-dot" />
          <span className="portal-header__divider-line" />
        </div>
      </div>
    </header>
  );
}

function SearchBar({ value, onChange, resultCount }) {
  return (
    <div className="search" role="search">
      <div className="search__field">
        <span className="search__icon"><SearchIcon /></span>
        <input
          type="search"
          className="search__input"
          placeholder="Search applications..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Search applications"
        />
        {value && (
          <button
            type="button"
            className="search__clear"
            onClick={() => onChange('')}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>
      <p className="search__meta" aria-live="polite">
        {resultCount} {resultCount === 1 ? 'application' : 'applications'}
      </p>
    </div>
  );
}

function CategoryFilter({ active, onChange }) {
  return (
    <nav className="categories" aria-label="Filter by category">
      <ul className="categories__list">
        {CATEGORIES.map((cat) => (
          <li key={cat.id}>
            <button
              type="button"
              className={`categories__btn ${active === cat.id ? 'categories__btn--active' : ''}`}
              onClick={() => onChange(cat.id)}
              aria-pressed={active === cat.id}
            >
              {cat.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function AppBadge({ app }) {
  if (app.isAndroidApp) {
    return (
      <span className="badge badge--android">
        <AndroidIcon />
        Android
      </span>
    );
  }
  if (app.isLocal) {
    return <span className="badge badge--local">Campus</span>;
  }
  return null;
}

function AppCard({ app }) {
  const link = app.isAndroidApp ? app.apkFile : app.url;

  const handleDownload = () => {
    const anchor = document.createElement('a');
    anchor.href = app.apkFile;
    anchor.download = `${app.name.replace(/\s+/g, '-').toLowerCase()}.apk`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const mainContent = (
    <>
      <div className="card__logo-wrap">
        <img src={app.logo} alt="" className="card__logo" />
      </div>
      <div className="card__body">
        <div className="card__top">
          <h3 className="card__title">{app.name}</h3>
          <AppBadge app={app} />
        </div>
        <p className="card__desc">{app.description}</p>
      </div>
      <span className="card__action" aria-hidden="true">
        {app.isAndroidApp ? (
          <>
            <DownloadIcon />
            <span>Download</span>
          </>
        ) : (
          <>
            <span>Open</span>
            <ExternalIcon />
          </>
        )}
      </span>
    </>
  );

  return (
    <div className={`card ${app.isAndroidApp ? 'card--android' : ''}`}>
      {app.isAndroidApp ? (
        <button type="button" className="card__main" onClick={handleDownload}>
          {mainContent}
        </button>
      ) : (
        <a
          href={app.url}
          className="card__main"
          target="_blank"
          rel="noopener noreferrer"
        >
          {mainContent}
        </a>
      )}
      <LinkActions url={link} title={app.name} variant="card" />
    </div>
  );
}

function EmptyState({ searchTerm, category }) {
  const categoryLabel = CATEGORIES.find((c) => c.id === category)?.label;
  return (
    <div className="empty" role="status">
      <div className="empty__icon">
        <SearchIcon />
      </div>
      <h2 className="empty__title">No applications found</h2>
      <p className="empty__text">
        {searchTerm
          ? `No results for "${searchTerm}"${category !== 'all' ? ` in ${categoryLabel}` : ''}.`
          : `No applications in ${categoryLabel}.`}
      </p>
    </div>
  );
}

function Footer() {
  return (
    <footer className="portal-footer">
      <p className="portal-footer__dept">IT Department</p>
      <p className="portal-footer__univ">
        AROR University of Art, Architecture, Design &amp; Heritage, Sukkur, Sindh
      </p>
      <p className="portal-footer__copy">Copyright © {new Date().getFullYear()}</p>
    </footer>
  );
}

function App() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredApps = useMemo(() => {
    const query = searchTerm.toLowerCase().trim();
    return apps.filter((app) => {
      const matchesCategory = activeCategory === 'all' || app.category === activeCategory;
      const matchesSearch =
        !query ||
        app.name.toLowerCase().includes(query) ||
        app.description.toLowerCase().includes(query);
      return matchesCategory && matchesSearch;
    });
  }, [searchTerm, activeCategory]);

  return (
    <div className="portal">
      <div className="portal__bg" aria-hidden="true">
        <div className="portal__bg-orb portal__bg-orb--1" />
        <div className="portal__bg-orb portal__bg-orb--2" />
        <div className="portal__bg-pattern" />
      </div>

      <Header />

      <div className="portal__controls">
        <div className="portal__controls-inner">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            resultCount={filteredApps.length}
          />
          <CategoryFilter active={activeCategory} onChange={setActiveCategory} />
        </div>
      </div>

      <main className="portal__main">
        <div className="section-head">
          <h2 className="section-head__title">Available Applications</h2>
          <span className="section-head__line" aria-hidden="true" />
        </div>

        {filteredApps.length > 0 ? (
          <div className="grid">
            {filteredApps.map((app) => (
              <AppCard key={app.id} app={app} />
            ))}
          </div>
        ) : (
          <EmptyState searchTerm={searchTerm} category={activeCategory} />
        )}
      </main>

      <Footer />
    </div>
  );
}

export default App;
